/**
 * Configuração › Metas e feriados: a meta do mês de cada canal e os feriados locais da conta.
 *
 * Meta: a do cliente (editada aqui) nunca é trocada pela sugestão. Sem meta, a Berytus sugere (média das últimas
 * 4 semanas por tipo de dia × dias do mês, com o crescimento da conta); canal com 2 a 4 semanas de venda tem sugestão
 * provisória, e com menos de 2 semanas não tem. Aceitar grava a sugestão (ela continua acompanhando as vendas);
 * travar deixa o valor parado. A divisão por dia mostra o peso de cada dia, com feriado −30%, emenda −20% e eleição
 * −20% (o que sai desses dias vai para os outros).
 * Feriados: os nacionais vêm da Berytus; os estaduais e municipais são cadastrados pela conta.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, brl, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { useMe } from '../components/AuthGate.tsx';
import { can } from '@berytus/contracts';

interface Goal {
  channel: string; name: string; amount: number | null; source: 'client' | 'suggested' | 'calculated' | null; provisional: boolean; locked: boolean; reason: string | null;
  days: { date: string; amount: number; discount: number }[];
}
interface Holiday { id: number; date: string; scope: string; uf: string | null; city: string | null; name: string; kind: string; mine: boolean }
interface GoalsData { month: string; growthPct: number; goals: Goal[]; holidays: Holiday[] }

const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const shift = (m: string, k: number) => { const d = new Date(Date.UTC(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1 + k, 1)); return d.toISOString().slice(0, 10); };
const SOURCE: Record<string, { label: string; tone: string }> = {
  client: { label: 'definida pela conta', tone: 'ok' }, suggested: { label: 'sugestão aceita', tone: 'info' }, calculated: { label: 'sugerida', tone: 'off' },
};

export function GoalsPage() {
  const me = useMe();
  const canEdit = can(me.tenant.access, 'settings', 'write');
  const qc = useQueryClient();
  const [month, setMonth] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['goals', month], queryFn: () => api<GoalsData>(`/goals${month ? `?month=${month}` : ''}`) });
  const done = () => { qc.invalidateQueries({ queryKey: ['goals'] }); qc.invalidateQueries({ queryKey: ['general'] }); };
  const act = useMutation({ mutationFn: (v: { path: string; method?: string; json?: unknown }) => api(v.path, { method: v.method ?? 'PUT', json: v.json ?? {} }), onSuccess: done });
  const [open, setOpen] = useState<string | null>(null);
  const d = q.data;
  const m = d?.month ?? month;
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Metas e feriados</h1>
          <p>Meta do mês por canal (a sua nunca é trocada pela sugestão) e os feriados locais, que entram na projeção e na divisão da meta por dia.</p>
        </div>
      </div>
      {q.error ? <ErrorBox error={q.error} /> : !d ? <Loading /> : (
        <div className="stack-y">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="btn sm" onClick={() => setMonth(shift(m!, -1))}>‹</button>
            <b style={{ minWidth: 140, textAlign: 'center' }}>{MONTHS[Number(m!.slice(5, 7)) - 1]} de {m!.slice(0, 4)}</b>
            <button type="button" className="btn sm" onClick={() => setMonth(shift(m!, 1))}>›</button>
            <span className="grow" />
            <span className="small muted">Crescimento da sugestão: <b>{d.growthPct.toLocaleString('pt-BR')}%</b></span>
            {canEdit && <button type="button" className="btn sm quiet" disabled={act.isPending} onClick={() => {
              const v = window.prompt('Crescimento da meta sugerida, em % (ex.: 5 ou −3):', String(d.growthPct));
              if (v != null && /^-?\d+([.,]\d+)?$/.test(v.trim())) act.mutate({ path: '/goals/growth', json: { pct: Number(v.trim().replace(',', '.')) } });
            }}>Mudar</button>}
          </div>

          <section className="panel flush">
            <div className="grid-wrap">
              <table className="grid" style={{ width: '100%', minWidth: 760 }}>
                <thead><tr><th>Canal</th><th className="num">Meta do mês</th><th>Origem</th><th /></tr></thead>
                <tbody>
                  {d.goals.length === 0 && <tr><td colSpan={4} className="muted">Nenhum canal conectado.</td></tr>}
                  {d.goals.map((g) => (
                    <tr key={g.channel} style={{ cursor: 'default' }}>
                      <td><b>{g.name}</b></td>
                      <td className="num">{g.amount == null ? <span className="muted">sem meta</span> : <b>{brl(g.amount)}</b>}</td>
                      <td>
                        {g.source && <Tag tone={SOURCE[g.source]!.tone}>{SOURCE[g.source]!.label}</Tag>}{' '}
                        {g.provisional && <Tag tone="warn">provisória</Tag>}{' '}{g.locked && <Tag tone="info">travada</Tag>}
                        {g.reason && <div className="small muted">{g.reason}</div>}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          {g.days.length > 0 && <button type="button" className="btn sm quiet" onClick={() => setOpen(open === g.channel ? null : g.channel)}>{open === g.channel ? 'Fechar' : 'Ver por dia'}</button>}
                          {canEdit && <>
                            <button type="button" className="btn sm" disabled={act.isPending} onClick={() => {
                              const v = window.prompt(`Meta de ${MONTHS[Number(m!.slice(5, 7)) - 1]} para ${g.name} (R$):`, g.amount != null ? String(g.amount) : '');
                              if (v == null) return;
                              const n = Number(v.trim().replace(/\./g, '').replace(',', '.'));
                              if (Number.isFinite(n) && n >= 0) act.mutate({ path: '/goals', json: { channel: g.channel, month: m, action: 'set', amount: n } });
                            }}>Editar</button>
                            {g.source === 'calculated' && <button type="button" className="btn sm" disabled={act.isPending} onClick={() => act.mutate({ path: '/goals', json: { channel: g.channel, month: m, action: 'accept' } })}>Aceitar</button>}
                            {g.amount != null && !g.locked && g.source !== 'client' && <button type="button" className="btn sm" disabled={act.isPending} onClick={() => act.mutate({ path: '/goals', json: { channel: g.channel, month: m, action: 'lock' } })}>Travar</button>}
                            {g.locked && <button type="button" className="btn sm quiet" disabled={act.isPending} onClick={() => act.mutate({ path: '/goals', json: { channel: g.channel, month: m, action: 'unlock' } })}>Destravar</button>}
                            {(g.source === 'client' || g.source === 'suggested') && <button type="button" className="btn sm quiet" disabled={act.isPending}
                              onClick={() => { if (window.confirm(`Voltar a meta de ${g.name} para a sugestão da Berytus?`)) act.mutate({ path: '/goals', json: { channel: g.channel, month: m, action: 'clear' } }); }}>Usar a sugestão</button>}
                          </>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {open && (() => {
              const g = d.goals.find((x) => x.channel === open)!;
              const max = Math.max(1, ...g.days.map((x) => x.amount));
              return (
                <div style={{ padding: 16, borderTop: '1px solid var(--line)' }}>
                  <div className="small muted" style={{ marginBottom: 8 }}>{g.name}: meta de cada dia (peso do dia da semana pelas vendas das últimas 4 semanas; feriado −30%, emenda −20%, eleição −20%).</div>
                  <div style={{ overflowX: 'auto' }}>
                    <div className="month-chart" style={{ height: 140 }}>
                      {g.days.map((x) => (
                        <div key={x.date} className="col" title={`${x.date.slice(8, 10)}/${x.date.slice(5, 7)}: ${brl(x.amount)}${x.discount ? ` (−${Math.round(x.discount * 100)}%)` : ''}`}>
                          <div className="stack" style={{ height: 120 }}><div style={{ height: `${(x.amount / max) * 120}px`, background: x.discount ? 'var(--warn)' : 'var(--chart-1)' }} /></div>
                          <span className="mono">{Number(x.date.slice(8, 10))}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}
          </section>
          {act.error && <ErrorBox error={act.error} />}

          <Holidays month={m!} list={d.holidays} canEdit={canEdit} onDone={done} />
        </div>
      )}
    </>
  );
}

function Holidays({ month, list, canEdit, onDone }: { month: string; list: Holiday[]; canEdit: boolean; onDone: () => void }) {
  const [f, setF] = useState({ date: '', scope: 'city', uf: '', city: '', name: '', kind: 'feriado' });
  const add = useMutation({ mutationFn: () => api('/goals/holidays', { method: 'POST', json: { ...f, uf: f.uf || undefined, city: f.scope === 'city' ? f.city : undefined } }), onSuccess: () => { setF({ ...f, date: '', name: '' }); onDone(); } });
  const del = useMutation({ mutationFn: (id: number) => api(`/goals/holidays/${id}`, { method: 'DELETE' }), onSuccess: onDone });
  return (
    <section className="panel stack-y" style={{ gap: 10 }}>
      <h2 style={{ margin: 0 }}>Feriados de {MONTHS[Number(month.slice(5, 7)) - 1]}</h2>
      {list.length === 0 ? <p className="small muted" style={{ margin: 0 }}>Nenhum feriado neste mês.</p> : list.map((h) => (
        <div key={h.id} className="row-line">
          <span><span className="mono">{h.date.slice(8, 10)}/{h.date.slice(5, 7)}</span> · {h.name} {h.kind === 'eleicao' && <Tag tone="info">eleição</Tag>}{' '}
            <span className="small muted">{h.scope === 'national' ? 'nacional' : h.scope === 'state' ? `estadual${h.uf ? ` (${h.uf})` : ''}` : `municipal${h.city ? ` (${h.city}${h.uf ? `/${h.uf}` : ''})` : ''}`}</span></span>
          {h.mine && canEdit ? <button type="button" className="btn sm quiet" disabled={del.isPending} onClick={() => del.mutate(h.id)}>Remover</button> : <span />}
        </div>
      ))}
      {canEdit && (
        <form style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'end' }} onSubmit={(e) => { e.preventDefault(); add.mutate(); }}>
          <label className="small" style={{ display: 'grid', gap: 4 }}>Data<input className="input" type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></label>
          <label className="small" style={{ display: 'grid', gap: 4 }}>Tipo
            <select className="select" value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })}><option value="city">Municipal</option><option value="state">Estadual</option></select></label>
          <label className="small" style={{ display: 'grid', gap: 4 }}>UF<input className="input" maxLength={2} style={{ width: 60 }} value={f.uf} onChange={(e) => setF({ ...f, uf: e.target.value.toUpperCase() })} /></label>
          {f.scope === 'city' && <label className="small" style={{ display: 'grid', gap: 4 }}>Cidade<input className="input" required value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></label>}
          <label className="small" style={{ display: 'grid', gap: 4 }}>Nome<input className="input" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
          <label className="small" style={{ display: 'grid', gap: 4 }}>É
            <select className="select" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}><option value="feriado">Feriado</option><option value="eleicao">Eleição</option></select></label>
          <button type="submit" className="btn sm primary" disabled={add.isPending}>Adicionar feriado</button>
        </form>
      )}
      {(add.error || del.error) && <ErrorBox error={add.error ?? del.error} />}
      <p className="small muted" style={{ margin: 0 }}>{int(list.filter((h) => h.mine).length)} feriado(s) seu(s) neste mês. Feriado conta como fim de semana na projeção.</p>
    </section>
  );
}
